import {
  CALLOUT_CELLS,
  calloutInfoAt,
  calloutMask,
  callouts as resolve,
  hasLowerLevel,
  levelAt,
  mapInfo,
  radarToWorld,
  type Callout,
  type CalloutEdit,
  type Level,
  type MapId,
  type RadarBox,
  type ZoneArea,
} from "@69ly/maps";
import { AREAS } from "@69ly/shared";
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { MapSwitch } from "../components/MapSwitch.tsx";
import { AtPoint, pct, Radar } from "../components/Radar.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { useCalloutEdits, useSaveCallouts } from "../lib/callouts.ts";
import { errorText } from "../lib/errors.ts";
import { useMe } from "../lib/queries.ts";

const NONE: CalloutEdit[] = [];

/** The radar point under the pointer, from the zoomed layer's box. */
function pointIn(layer: Element | null, e: { clientX: number; clientY: number }) {
  if (!layer) return null;
  const r = layer.getBoundingClientRect();
  const u = (e.clientX - r.left) / r.width;
  const v = (e.clientY - r.top) / r.height;
  return u < 0 || v < 0 || u > 1 || v > 1 ? null : { u, v };
}
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** The covered cells of the selected callout, drawn under the labels. */
function Coverage({ mask }: { mask: Uint8Array }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = ref.current?.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(CALLOUT_CELLS, CALLOUT_CELLS);
    for (let i = 0; i < mask.length; i++) {
      if (!mask[i]) continue;
      img.data.set([90, 176, 255, 110], i * 4);
    }
    ctx.putImageData(img, 0, 0);
  }, [mask]);
  return <canvas ref={ref} className="callout-coverage" width={CALLOUT_CELLS} height={CALLOUT_CELLS} aria-hidden="true" />;
}

/** One label on the map: drag to move, click to select. */
function EditLabel({ c, selected, onSelect, onMove }: { c: Callout; selected: boolean; onSelect: () => void; onMove: (u: number, v: number) => void }) {
  const drag = useRef<{ x: number; y: number; moved: boolean; layer: Element | null } | null>(null);
  const [at, setAt] = useState<{ u: number; v: number } | null>(null);
  const down = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, moved: false, layer: e.currentTarget.closest(".radar-layer") };
  };
  const move = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 4) return;
    d.moved = true;
    const r = d.layer?.getBoundingClientRect();
    if (r) setAt({ u: clamp01((e.clientX - r.left) / r.width), v: clamp01((e.clientY - r.top) / r.height) });
  };
  const up = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const d = drag.current;
    drag.current = null;
    if (d?.moved && at) onMove(at.u, at.v);
    else onSelect();
    setAt(null);
  };
  return (
    <AtPoint u={at?.u ?? c.u} v={at?.v ?? c.v} className="callout-spot is-editable">
      <button
        type="button"
        className={`callout callout-edit area-${c.area}${selected ? " is-selected" : ""}${c.hidden ? " is-hidden" : ""}${c.edited ? " is-edited" : ""}`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onClick={(e) => e.stopPropagation()}
        aria-pressed={selected}
      >
        {c.name}
      </button>
    </AtPoint>
  );
}

/** Drawing a box: press, drag, release. */
function DrawLayer({ onBox }: { onBox: (b: RadarBox) => void }) {
  const start = useRef<{ u: number; v: number } | null>(null);
  const [box, setBox] = useState<RadarBox | null>(null);
  const span = (a: { u: number; v: number }, b: { u: number; v: number }): RadarBox => ({
    u0: Math.min(a.u, b.u),
    v0: Math.min(a.v, b.v),
    u1: Math.max(a.u, b.u),
    v1: Math.max(a.v, b.v),
  });
  return (
    <div
      className="callout-draw"
      onPointerDown={(e) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        start.current = pointIn(e.currentTarget.parentElement, e);
      }}
      onPointerMove={(e) => {
        const p = pointIn(e.currentTarget.parentElement, e);
        if (start.current && p) setBox(span(start.current, p));
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        const p = pointIn(e.currentTarget.parentElement, e);
        if (start.current && p) {
          const b = span(start.current, p);
          if (b.u1 - b.u0 > 0.006 && b.v1 - b.v0 > 0.006) onBox(b);
        }
        start.current = null;
        setBox(null);
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {box && <div className="callout-box is-drawing" style={{ left: pct(box.u0), top: pct(box.v0), width: pct(box.u1 - box.u0), height: pct(box.v1 - box.v0) }} />}
    </div>
  );
}

/** Same changes, whatever order the keys come in (the database sorts a box's keys). */
const canon = (list: CalloutEdit[]) =>
  JSON.stringify(list.map((e) => [e.level, e.base, e.ordinal, e.name, e.u, e.v, e.area, e.hidden, e.zone && [e.zone.u0, e.zone.v0, e.zone.u1, e.zone.v1]]));
const sameEdits = (a: CalloutEdit[], b: CalloutEdit[]) => canon(a) === canon(b);

function Editor({ map, saved, onDirty }: { map: MapId; saved: CalloutEdit[]; onDirty: (dirty: boolean) => void }) {
  const { t } = useTranslation();
  const save = useSaveCallouts();
  const [draft, setDraft] = useState<CalloutEdit[]>(saved);
  const [level, setLevel] = useState<Level>("upper");
  const [selected, setSelected] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<false | "new" | string>(false);
  const [hover, setHover] = useState<Callout | null>(null);
  const [filter, setFilter] = useState("");
  const [savedAt, setSavedAt] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const dirty = !sameEdits(draft, saved);

  const list = useMemo(() => resolve(map, level, draft), [map, level, draft]);
  const sel = list.find((c) => c.key === selected) ?? null;
  const mask = useMemo(() => (sel ? calloutMask(map, level, sel.key, draft) : null), [map, level, sel, draft]);
  const info = mapInfo(map);

  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** Where a callout's change lives in the draft (Valve's: by place and ordinal; ours: the n-th own one on this floor). */
  const indexOf = (c: Callout) => {
    if (c.base !== null) return draft.findIndex((e) => e.level === level && e.base === c.base && e.ordinal === c.ordinal);
    const n = Number(c.key.slice("own:".length));
    const own = draft.map((e, i) => ({ e, i })).filter(({ e }) => e.base === null && e.level === level);
    return own[n]?.i ?? -1;
  };
  const editOf = (c: Callout): CalloutEdit => ({
    level,
    base: c.base,
    ordinal: c.ordinal,
    name: c.name,
    u: c.u,
    v: c.v,
    area: c.area === c.defaultArea && (draft[indexOf(c)]?.area ?? null) === null ? null : c.area,
    hidden: c.hidden,
    zone: c.base === null ? c.zone : null,
  });
  /** Valve's label as it came: a change back to it removes the change. */
  const isPlain = (c: Callout, e: CalloutEdit) => {
    if (c.base === null) return false;
    const orig = resolve(map, level, NONE).find((x) => x.key === c.key);
    return !!orig && e.name === orig.name && e.u === orig.u && e.v === orig.v && e.area === null && !e.hidden;
  };
  const change = (c: Callout, patch: Partial<CalloutEdit>) => {
    const i = indexOf(c);
    const next = { ...editOf(c), ...patch };
    const copy = [...draft];
    if (isPlain(c, next)) {
      if (i >= 0) copy.splice(i, 1);
    } else if (i >= 0) copy[i] = next;
    else copy.push(next);
    setDraft(copy);
  };
  const removeOwn = (c: Callout) => {
    const i = indexOf(c);
    if (i >= 0) setDraft(draft.filter((_, j) => j !== i));
    setSelected(null);
  };
  const addOwn = (b: RadarBox) => {
    if (drawing && drawing !== "new" && sel?.base === null) {
      change(sel, { zone: b, u: (b.u0 + b.u1) / 2, v: (b.v0 + b.v1) / 2 });
      setDrawing(false);
      return;
    }
    const own = draft.filter((e) => e.base === null && e.level === level).length;
    setDraft([...draft, { level, base: null, ordinal: 0, name: t("calloutAdmin.newName"), u: (b.u0 + b.u1) / 2, v: (b.v0 + b.v1) / 2, area: null, hidden: false, zone: b }]);
    setSelected(`own:${own}`);
    setDrawing(false);
    setTimeout(() => nameInput.current?.select(), 0);
  };

  const onHover = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = pointIn(frame.current?.querySelector(".radar-layer") ?? null, e);
    if (!p) return setHover(null);
    const w = radarToWorld(map, p);
    const lower = info.lowerBelowZ;
    const z = level === "lower" && lower !== undefined ? lower - 100 : null;
    setHover(levelAt(map, z) === level ? calloutInfoAt(map, w.x, w.y, z, draft) : null);
  };

  const shown = list.filter((c) => !filter.trim() || c.name.toLowerCase().includes(filter.trim().toLowerCase()) || c.base?.toLowerCase().includes(filter.trim().toLowerCase()));
  const changes = draft.length;

  return (
    <div className="columns callout-admin">
      <div className="column-main">
        <div className="callout-toolbar">
          {hasLowerLevel(map) && (
            <div className="segmented" role="group" aria-label={t("level.label")}>
              {(["upper", "lower"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  className="seg"
                  aria-pressed={level === l}
                  onClick={() => {
                    setLevel(l);
                    setSelected(null);
                  }}
                >
                  {t(`level.${l}`)}
                </button>
              ))}
            </div>
          )}
          <button type="button" className={`btn ${drawing === "new" ? "btn-primary" : "btn-secondary"}`} aria-pressed={drawing === "new"} onClick={() => setDrawing(drawing ? false : "new")}>
            {drawing === "new" ? t("calloutAdmin.drawing") : t("calloutAdmin.add")}
          </button>
          <p className="muted small callout-readout" aria-live="polite">
            {hover ? t("calloutAdmin.here", { name: hover.name, area: t(`area.${hover.area}`) }) : t("calloutAdmin.hoverHelp")}
          </p>
        </div>
        <section className="map-frame callout-map" aria-label={info.name} ref={frame} onPointerMove={onHover} onPointerLeave={() => setHover(null)}>
          <Radar map={map} mapName={info.name} level={level} callouts={false} onBackground={() => setSelected(null)}>
            {mask && <Coverage mask={mask} />}
            {list
              .filter((c) => c.zone && c.base === null)
              .map((c) => (
                <div
                  key={`box-${c.key}`}
                  className={`callout-box${c.key === selected ? " is-selected" : ""}`}
                  style={{ left: pct(c.zone!.u0), top: pct(c.zone!.v0), width: pct(c.zone!.u1 - c.zone!.u0), height: pct(c.zone!.v1 - c.zone!.v0) }}
                />
              ))}
            <div className="callouts">
              {list.map((c) => (
                <EditLabel
                  key={c.key}
                  c={c}
                  selected={c.key === selected}
                  onSelect={() => setSelected(c.key)}
                  onMove={(u, v) => {
                    setSelected(c.key);
                    change(c, { u, v });
                  }}
                />
              ))}
            </div>
            {drawing && <DrawLayer onBox={addOwn} />}
          </Radar>
        </section>
        <ul className="callout-legend" aria-label={t("form.area")}>
          {AREAS.map((a) => (
            <li key={a} className={`area-${a}`}>
              {t(`area.${a}`)}
            </li>
          ))}
        </ul>
        <p className="muted small">{t("calloutAdmin.mapHelp")}</p>
      </div>

      <aside className="column-side">
        <section className="card callout-save">
          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate(
                  { map, edits: draft },
                  {
                    onSuccess: () => {
                      setSavedAt(true);
                      setTimeout(() => setSavedAt(false), 2500);
                    },
                  },
                )
              }
            >
              {save.isPending ? t("calloutAdmin.saving") : t("calloutAdmin.save")}
            </button>
            <button type="button" className="btn btn-quiet" disabled={!dirty} onClick={() => setDraft(saved)}>
              {t("calloutAdmin.discard")}
            </button>
          </div>
          <p className="muted small">
            {savedAt ? t("calloutAdmin.saved") : dirty ? t("calloutAdmin.unsaved") : t("calloutAdmin.changes", { count: changes })}
          </p>
          {save.error && <p className="field-error small">{errorText(t, save.error)}</p>}
        </section>

        {sel ? (
          <section className="card callout-form" aria-label={t("calloutAdmin.selected")}>
            <div className="row callout-form-head">
              <h2 className="panel-title">{sel.name}</h2>
              <span className="muted small">{sel.base === null ? t("calloutAdmin.own") : sel.base !== sel.name ? t("calloutAdmin.valveWas", { name: sel.base }) : t("calloutAdmin.valve")}</span>
            </div>
            <label className="field">
              <span>{t("calloutAdmin.name")}</span>
              <input ref={nameInput} value={sel.name} maxLength={40} onChange={(e) => change(sel, { name: e.target.value })} />
            </label>
            <div className="field">
              <span>{t("form.area")}</span>
              <div className="segmented" role="group" aria-label={t("form.area")}>
                {AREAS.map((a) => (
                  <button key={a} type="button" className="seg" aria-pressed={sel.area === a} onClick={() => change(sel, { area: a === sel.defaultArea && sel.base !== null ? null : (a as ZoneArea) })}>
                    {t(`areaShort.${a}`)}
                    {a === sel.defaultArea && <span className="auto-tag">{t("areaField.auto")}</span>}
                  </button>
                ))}
              </div>
            </div>
            <label className="check">
              <input type="checkbox" checked={sel.hidden} onChange={(e) => change(sel, { hidden: e.target.checked })} />
              <span>{t("calloutAdmin.hide")}</span>
            </label>
            <p className="muted small">{sel.base === null ? t("calloutAdmin.ownHelp") : t("calloutAdmin.valveHelp")}</p>
            <div className="row">
              {sel.base === null ? (
                <>
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => setDrawing(sel.key)}>
                    {drawing === sel.key ? t("calloutAdmin.drawing") : t("calloutAdmin.redraw")}
                  </button>
                  <button type="button" className="btn btn-quiet btn-danger btn-small" onClick={() => removeOwn(sel)}>
                    {t("calloutAdmin.delete")}
                  </button>
                </>
              ) : (
                sel.edited && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-small"
                    onClick={() => {
                      const i = indexOf(sel);
                      if (i >= 0) setDraft(draft.filter((_, j) => j !== i));
                    }}
                  >
                    {t("calloutAdmin.reset")}
                  </button>
                )
              )}
            </div>
          </section>
        ) : (
          <p className="muted small callout-hint">{t("calloutAdmin.pick")}</p>
        )}

        <section className="card callout-list-card">
          <label className="field">
            <span>{t("calloutAdmin.find", { count: list.length })}</span>
            <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("calloutAdmin.findPlaceholder")} />
          </label>
          <ul className="callout-list">
            {shown.map((c) => (
              <li key={c.key}>
                <button type="button" className={`callout-row${c.key === selected ? " is-selected" : ""}`} onClick={() => setSelected(c.key)}>
                  <span className={c.hidden ? "is-hidden-name" : undefined}>{c.name}</span>
                  <span className="muted small">
                    {t(`areaShort.${c.area}`)}
                    {c.hidden ? ` · ${t("calloutAdmin.hidden")}` : ""}
                    {c.base === null ? ` · ${t("calloutAdmin.ownShort")}` : c.edited ? ` · ${t("calloutAdmin.edited")}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </aside>
    </div>
  );
}

/** Admins only: rename, move, re-area, hide and add map callouts. */
export function CalloutAdminPage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const edits = useCalloutEdits();
  const [map, setMap] = useState<MapId>("de_mirage");
  const [pending, setPending] = useState<MapId | null>(null);
  const dirtyRef = useRef(false);

  if (!me?.isAdmin) return <Navigate to="/library" replace />;
  if (edits.isPending) return <Loading />;
  if (edits.error) return <ErrorState message={errorText(t, edits.error)} onRetry={() => void edits.refetch()} />;
  const saved = (edits.data.maps[map] as CalloutEdit[] | undefined) ?? NONE;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t("calloutAdmin.title")}</h1>
          <p className="muted">{t("calloutAdmin.intro")}</p>
        </div>
        <MapSwitch
          map={map}
          onPick={(m) => {
            if (dirtyRef.current) setPending(m);
            else setMap(m);
          }}
        />
      </div>
      {pending && (
        <Banner tone="warning">
          {t("calloutAdmin.leave", { map: mapInfo(map).name })}{" "}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setMap(pending);
              setPending(null);
            }}
          >
            {t("calloutAdmin.leaveAnyway", { map: mapInfo(pending).name })}
          </button>{" "}
          <button type="button" className="link-button" onClick={() => setPending(null)}>
            {t("common.cancel")}
          </button>
        </Banner>
      )}
      <Editor key={map} map={map} saved={saved} onDirty={(d) => (dirtyRef.current = d)} />
    </div>
  );
}
